/**
 * The Journal as a book (journal redesign 2026-10-27,
 * `Claude outputs/journal-redesign-2026-10-27/DESIGN.md`).
 *
 * Each day is a dated page holding only what happened, in diary order. A day
 * with nothing on it never appears. These are the pure helpers the book's
 * components share: reading the server's payload (`normalizeBook`, the one
 * place the contract in API.md is mapped), the words of each ink line, the
 * calendar, search and the page order.
 *
 * Words are returned as parts ({ text, em }) and rendered as text, never as
 * HTML: titles come from other people (a gift's title, a lesson's title).
 */
import { todayKey } from '@/utils/journal-days'

const MON = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const WK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const p2 = (n) => String(n).padStart(2, '0')

// ── dates ───────────────────────────────────────────────────────────────────
export const keyDate = (k) => { const [y, m, d] = String(k).split('-').map(Number); return new Date(y, m - 1, d) }
export const dateKey = (dt) => `${dt.getFullYear()}-${p2(dt.getMonth() + 1)}-${p2(dt.getDate())}`
export const addDays = (k, n) => { const d = keyDate(k); d.setDate(d.getDate() + n); return dateKey(d) }
export const shortDate = (k) => { const d = keyDate(k); return `${MON[d.getMonth()].slice(0, 3)} ${d.getDate()}` }
export const longDate = (k) => { const d = keyDate(k); return { weekday: WK[d.getDay()], date: `${MON[d.getMonth()]} ${d.getDate()}` } }
export const fullDate = (k) => { const d = keyDate(k); return `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` }
export const weekdayShort = (k) => WK[keyDate(k).getDay()].slice(0, 3).toUpperCase()
export const monthTitle = (k) => { const d = keyDate(k); return `${MON[d.getMonth()]} ${d.getFullYear()}` }

export function relLabel (key, today) {
  if (key === today) return 'Today'
  if (key === addDays(today, -1)) return 'Yesterday'
  return ''
}

/** "8:12 am" — the time written in the margin. */
export function fmtTime (iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const h = d.getHours()
  return `${h % 12 || 12}:${p2(d.getMinutes())} ${h < 12 ? 'am' : 'pm'}`
}

/** The browser's IANA time zone, sent with every Journal request (API.md). */
export function browserTimeZone () {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch (e) { return 'UTC' }
}

// ── the payload (API.md v1.1: GET journal/days/, GET journal/status/) ────────
// Contract type → the page's kind.
const KIND = {
  altar_visit: 'altar', horoscope: 'horo', intention: 'intent', altar_painted: 'paint', reading: 'reading', talk: 'talk', letter: 'letter',
  gift_received: 'gift_in', gift_sent: 'gift_out', circle_joined: 'circle', compatibility: 'compat', birthday: 'bday',
  mystic_message: 'msg', followed: 'follow', image_saved: 'saved', image_shared: 'saved', voted: 'vote',
  lesson_spoke_to_you: 'lesson_in', lesson_spoke_to_others: 'lesson_out', lesson_shared: 'lesson_share', look_conjured: 'look',
  message_sent: 'message_sent', comment: 'comment',
  // Keep (mystic page 2026-10-29, API.md §6): a kept picture is taped in like a Mystic's message.
  kept: 'msg',
}
const person = (x) => (x && typeof x === 'object') ? { name: x.name || '', avatar: x.avatar || '' } : { name: String(x || ''), avatar: '' }
const mystic = (x) => (x && typeof x === 'object')
  ? { name: x.name || 'Mystic Aida', sub: x.sub_domain || x.sub || '', avatar: x.avatar || '' }
  : null
const cardOf = (c) => (!c ? null : typeof c === 'string' ? { name: c, image: '' } : { name: (c.name || '') + (c.reversed ? ' (reversed)' : ''), image: c.image_url || '' })

/** "08:13" (the server's local_time) → "8:13 am". */
export function fmtHM (hm) {
  const m = String(hm || '').match(/^(\d{1,2}):(\d{2})/)
  if (!m) return ''
  const h = Number(m[1])
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'am' : 'pm'}`
}

export function normalizeItem (it, i = 0, dayKey = '') {
  const d = it.data || {}
  const m = {
    id: String(it.id || `${dayKey}-${i}`), type: it.type, kind: KIND[it.type] || it.type, group: it.group || '',
    at: it.at || '', time: it.local_time ? fmtHM(it.local_time) : fmtTime(it.at), hidden: !!it.hidden, line: it.line || '',
    title: d.title || '', text: d.text || '', mystic: mystic(d.mystic), from: null, to: null, who: null, card: null, cards: [],
  }
  switch (it.type) {
    case 'intention': m.value = d.text || ''; m.text = ''; break
    case 'reading': {
      const o = d.open || {}
      Object.assign(m, { question: d.question || '', cards: (d.cards || []).map(cardOf), rating: d.rating || 0, quote: d.line || '',
        reading_id: o.id || d.id || '', reader_sub: o.reader || (m.mystic && m.mystic.sub) || '',
        // The practice: their hunch, the thread, and who a reading done for someone was for.
        gut: d.gut || '', threadId: d.thread_id === undefined ? null : d.thread_id, forName: d.for_name ? String(d.for_name) : '' })
      // A card drawn from a lesson (lesson-redo K3): one line back to the lesson.
      const fl = d.from_lesson
      m.from_lesson = fl && fl.lesson_id ? { id: String(fl.lesson_id), about: String(fl.about || '').trim() } : null
      break
    }
    case 'talk': m.reading_id = d.reading_id || ''; m.reader_sub = (m.mystic && m.mystic.sub) || ''; break
    case 'gift_received': {
      const o = d.open || {}
      Object.assign(m, { from: person(d.from_name), card: cardOf(d.pulled_card || d.card), image_url: d.image_url || '',
        reading_id: o.reading || d.reading_id || '', invite: o.inv || d.invite || '', reader_sub: (m.mystic && m.mystic.sub) || '' })
      break
    }
    case 'gift_sent':
      Object.assign(m, { to: person(d.to_name), card: cardOf(d.card), image_url: d.image_url || '', reading_id: d.reading_id || '', invite: d.invite || '',
        opened_at: d.opened ? (d.opened_at || 'opened') : null, thanked: !!d.thanked, revoked_at: d.taken_back ? 'taken' : null, revocable: !!d.can_take_back })
      break
    case 'circle_joined': case 'compatibility': case 'birthday': m.who = person(d.person); m.direction = d.direction || ''; break
    case 'mystic_message': m.image_url = d.image_url || d.preview_image_url || ''; m.text = d.text || d.teaser || ''; break
    case 'image_saved': case 'image_shared': m.image_url = d.image_url || ''; break
    case 'kept': {
      // The giver's mark: square for a Mystic, round for a seeker.
      const g = d.giver && typeof d.giver === 'object' ? d.giver : {}
      Object.assign(m, { image_url: d.image_url || '', text: d.caption || '', source: d.source || '' })
      if (g.kind === 'seeker') { m.mystic = null; m.from = { name: g.name || '', avatar: g.portrait || '' } } else m.mystic = { name: g.name || 'Mystic Aida', sub: g.sub_domain || '', avatar: g.portrait || '' }
      break
    }
    case 'lesson_spoke_to_you': m.who = { name: d.name || '', avatar: '' }; break
    case 'lesson_spoke_to_others': m.count = d.count || 0; break
    case 'message_sent': m.who = person(d.person); break
  }
  return m
}

function normalizeEntry (e) {
  if (!e || !(e.summary || e.text)) return null
  const it = e.intention && typeof e.intention === 'object' ? e.intention : null
  return {
    text: e.summary || e.text || '',
    edited: e.written_overnight === false,
    hasVersions: !!e.has_versions,
    intention: (it && it.text) || '',
    answer: (it && it.answer) || null,
    unseen: e.seen === false,
  }
}

// ── the practice (journal practice 2026-10-28, API.md v1): threads, own lines, before ──
// Thread ids are integers on the wire; they are kept as they come and sent back as they came.
const hasId = (x) => x !== null && x !== undefined && x !== ''
export const normalizeThreadTag = (t, i = 0) => ({ id: t.id, name: String(t.name || ''), main: typeof t.main === 'boolean' ? t.main : i === 0, nameIsUser: !!t.name_is_user })
function normalizeOwn (o) {
  if (!o || typeof o !== 'object') return null
  return { readingId: String(o.reading_id || ''), threadId: hasId(o.thread_id) ? o.thread_id : null, question: String(o.question || ''),
    forName: o.for_name ? String(o.for_name) : '', gut: String(o.gut || ''), landed: String(o.landed || ''), gutEdited: !!o.gut_edited, landedEdited: !!o.landed_edited }
}
function normalizeBefore (b) {
  if (!b || !b.line || !b.date) return null
  return { key: String(b.date), line: String(b.line), threadId: hasId(b.thread_id) ? b.thread_id : null }
}
/**
 * The left page's lines. A thread with no line of its own keeps its row (its
 * name, then its own question, quietly: one thread per quote, fix 2026-10-28);
 * a row with neither a thread nor a line is no row.
 */
function normalizeMeaning (d, threads, owns = []) {
  if (Array.isArray(d.meaning)) {
    const question = (tid) => { const o = [...owns].reverse().find((x) => x.threadId === tid && x.question); return o ? o.question : '' }
    return d.meaning.filter((m) => m && (m.line || hasId(m.thread_id)))
      .map((m) => {
        const threadId = hasId(m.thread_id) ? m.thread_id : null
        const out = { threadId, thread: String(m.thread || ''), line: String(m.line || ''), edited: !!m.edited }
        if (!out.line) out.question = question(threadId)
        return out
      })
  }
  if (d.meaning_line) {
    const t = threads.find((x) => x.main) || threads[0] || { id: null, name: '' }
    return [{ threadId: t.id, thread: t.name, line: String(d.meaning_line), edited: !!d.meaning_edited }]
  }
  return []
}

export function normalizeDay (d) {
  const key = d.date
  const moments = (d.items || []).map((it, i) => normalizeItem(it, i, key)).sort((a, b) => (a.at || '').localeCompare(b.at || ''))
  const threads = (Array.isArray(d.threads) ? d.threads : []).filter((t) => t && hasId(t.id)).map(normalizeThreadTag)
  const owns = (Array.isArray(d.owns) ? d.owns : []).map(normalizeOwn).filter(Boolean)
  const own = normalizeOwn(d.own)
  return {
    key, entry: normalizeEntry(d.entry), moments, hidden: !!d.hidden, hasGift: d.has_gift, highlights: Array.isArray(d.highlights) ? d.highlights : null, willBeWritten: d.will_be_written,
    // A reading done for someone is never the day's own (fix 2026-10-28).
    threads, own: own && !own.forName ? own : null, owns, before: normalizeBefore(d.before), meaning: normalizeMeaning(d, threads, owns),
  }
}

/** The seeker's entry length: the newest entry's word_count, else 80 (journal practice API v1.1). */
function entryLength (days = []) {
  const newest = [...days].filter((d) => d && d.entry && d.entry.word_count).sort((x, y) => String(y.date).localeCompare(String(x.date)))[0]
  return (newest && Number(newest.entry.word_count)) || 80
}

/**
 * The days response (one or more pages of it, merged) and the status → the
 * book the pages read. `status` is GET journal/status/ (lock, cover, unseen).
 */
export function normalizeBook (raw = {}, status = {}) {
  const today = raw.today || todayKey()
  const lock = (status && status.lock) || raw.lock || {}
  const days = (raw.days || []).map(normalizeDay).filter((d) => d.key && (d.moments.length || d.entry)).sort((a, b) => a.key.localeCompare(b.key))
  return {
    name: raw.name || '',
    begun: raw.begun || (days[0] && days[0].key) || today,
    today,
    email: '',
    wordCount: entryLength(raw.days),
    unseen: raw.unseen_date || (status && status.unseen_date) || null,
    nextBefore: raw.next_before || null,
    locked: !!raw.locked,
    coverOpened: status && 'cover_opened' in status ? !!status.cover_opened : true,
    clasp: { on: !!lock.on, passkey: !!lock.passkey, offered: lock.offered !== false },
    days,
    threads: [],          // GET journal/threads/ (the practice), loaded beside the days
    chapterStates: {},    // 'YYYY-MM' → journal/calendar/'s chapter state (null: never one)
  }
}

/** Merge an older page of days (next_before) into the book. */
export function mergeDays (book, raw) {
  const have = new Set(book.days.map((d) => d.key))
  const more = (raw.days || []).map(normalizeDay).filter((d) => d.key && !have.has(d.key) && (d.moments.length || d.entry))
  return { ...book, days: [...book.days, ...more].sort((a, b) => a.key.localeCompare(b.key)), nextBefore: raw.next_before || null, name: book.name || raw.name || '', begun: raw.begun || book.begun }
}

/** One redrawn page (GET journal/day/) into the book. */
export function withDay (book, rawDay) {
  if (!rawDay) return book
  const d = normalizeDay(rawDay)
  const rest = book.days.filter((x) => x.key !== d.key)
  return { ...book, days: [...rest, d].sort((a, b) => a.key.localeCompare(b.key)) }
}

// ── pages ───────────────────────────────────────────────────────────────────
export function pageKeys (book, { hiddenDays = {} } = {}) {
  return (book.days || []).filter((d) => !hiddenDays[d.key]).map((d) => d.key)
}

/** The hidden pages, as the { key: true } map the helpers take (none while they are being shown). */
export function hiddenMap (book, { showing = false } = {}) {
  const out = {}
  if (showing) return out
  for (const d of book.days || []) if (d.hidden) out[d.key] = true
  return out
}

export function neighbours (keys, key) {
  const i = keys.indexOf(key)
  return { prev: i > 0 ? keys[i - 1] : '', next: i >= 0 && i < keys.length - 1 ? keys[i + 1] : '' }
}

/** ?day= first (refresh, return from a reading), then an unread entry, then the newest page. */
export function openingDay (book, { initialDay = '', hiddenDays = {} } = {}) {
  const keys = pageKeys(book, { hiddenDays })
  if (initialDay && keys.includes(initialDay)) return initialDay
  if (book.unseen && keys.includes(book.unseen)) return book.unseen
  return keys[keys.length - 1] || ''
}

export const dayOf = (book, key) => (book.days || []).find((d) => d.key === key) || null

// ── words ───────────────────────────────────────────────────────────────────
const T = (text) => ({ text })
const I = (text) => ({ text, em: true })
const nm = (x, fallback = 'Someone') => (x && x.name) || fallback
const NUM = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']
const seekers = (n) => (n === 1 ? 'one seeker' : n > 1 ? `${NUM[n] || n} seekers` : 'seekers')

/** The emphasised words of a line (a title, a question), if the line has any. */
function emphasis (m) {
  switch (m.kind) {
    case 'horo': case 'saved': case 'lesson_in': case 'lesson_out': case 'lesson_share': return m.title
    case 'intent': return m.value
    case 'reading': return m.question
  }
  return ''
}

/** One ink line: the server's `line` (the design's copy), its title in italics. */
export function lineParts (m) {
  if (m.line) {
    const em = emphasis(m)
    const i = em ? m.line.lastIndexOf(em) : -1
    if (i < 0) return [T(m.line)]
    const out = []
    if (i > 0) out.push(T(m.line.slice(0, i)))
    out.push(I(em))
    if (i + em.length < m.line.length) out.push(T(m.line.slice(i + em.length)))
    return out
  }
  return fallbackParts(m)
}

function fallbackParts (m) {
  const my = nm(m.mystic, 'Mystic Aida')
  switch (m.kind) {
    case 'altar': return [T('Visited your Altar')]
    case 'horo': return [T('Read your horoscope: '), I(m.title || 'today’s stars')]
    case 'intent': return [T('Set your intention: '), I(m.value || '')]
    case 'paint': return [T('Painted your Altar')]
    case 'talk': return [T(`Talked it through with ${my}`)]
    case 'reading': return [T(`You asked ${my}: `), I(m.question || 'a question')]
    case 'letter': return [T(`A letter from ${my} arrived`)]
    case 'gift_in': return [T(`${nm(m.from)} read for you`)]
    case 'gift_out': return [T(`You read for ${nm(m.to)}`)]
    case 'circle': return [T(`${nm(m.who)} joined your Circle`)]
    case 'compat': return [T(`Revealed your compatibility with ${nm(m.who)}`)]
    case 'bday': return [T(`${nm(m.who)} shared a birthday with you`)]
    case 'msg': return [T(`${my} left you a message`)]
    case 'follow': return [T(`Followed ${my}`)]
    case 'saved': return [T('Saved an image: '), I(m.title || '')]
    case 'vote': return [T('Voted in the Voice for the next Mystic')]
    case 'lesson_in': return [T(`A lesson spoke to you: ${nm(m.who, 'a seeker')}’s `), I(m.title || '')]
    case 'lesson_out': return [T('Your lesson '), I(m.title || ''), T(` spoke to ${seekers(m.count)}`)]
    case 'look': return [T('Conjured a new look')]
  }
  return [T(m.text || '')]
}
export const lineText = (m) => lineParts(m).map((x) => x.text).join('')

// Which moments lead a day's line in the calendar.
const PRI = { gift_in: 1, gift_out: 2, reading: 3, letter: 4, circle: 5, compat: 5, bday: 5, msg: 6, lesson_in: 7, lesson_out: 7, vote: 7, follow: 8, saved: 8, look: 8, talk: 9, intent: 9, paint: 9, horo: 10, altar: 11 }
const pri = (k) => PRI[k] || 12

export function gist (day) {
  const ms = (day.moments || []).filter((m) => !m.hidden)
  if (day.highlights && day.highlights.length) return { text: day.highlights.slice(0, 2).join(' · '), more: Math.max(0, ms.length - 2) }
  const top = [...ms].sort((a, b) => pri(a.kind) - pri(b.kind)).slice(0, 2).map(lineText)
  return { text: top.join(' · '), more: Math.max(0, ms.length - 2) }
}

export const hasGift = (day) => (typeof day.hasGift === 'boolean' ? day.hasGift : (day.moments || []).some((m) => (m.kind === 'gift_in' || m.kind === 'gift_out') && !m.hidden))

/** The day touched the thread: tagged with it, a reading on it, or a line under it. */
export function dayOnThread (day, id) {
  if (!day || !hasId(id)) return false
  return (day.threads || []).some((t) => t.id === id) || (day.owns || []).some((o) => o.threadId === id) || (day.meaning || []).some((m) => m.threadId === id)
}

/** The days of a month as grid cells: blanks to the weekday, then each day. With
 * `threadId` (the thread open on the right, DESIGN §8) its pages are `lit` (ringed). */
export function monthGrid (anyKeyInMonth, book, { today = '', cur = '', hiddenDays = {}, threadId = null } = {}) {
  const d0 = keyDate(anyKeyInMonth)
  const y = d0.getFullYear(); const m = d0.getMonth()
  const start = new Date(y, m, 1).getDay()
  const n = new Date(y, m + 1, 0).getDate()
  const byKey = new Map((book.days || []).filter((d) => !hiddenDays[d.key]).map((d) => [d.key, d]))
  const cells = []
  for (let i = 0; i < start; i++) cells.push({ day: 0 })
  for (let d = 1; d <= n; d++) {
    const key = dateKey(new Date(y, m, d))
    const page = byKey.get(key)
    cells.push({ day: d, key, has: !!page, gift: !!page && hasGift(page), today: key === today, sel: key === cur, lit: !!page && hasId(threadId) && dayOnThread(page, threadId) })
  }
  return cells
}

export const monthKey = (k) => k.slice(0, 7) + '-01'
export const addMonths = (k, n) => { const d = keyDate(monthKey(k)); d.setMonth(d.getMonth() + n); return dateKey(d) }

/** Pages in a month, newest first, for the at-a-glance list (only a thread's, with `threadId`). */
export function monthPages (anyKeyInMonth, book, { hiddenDays = {}, threadId = null } = {}) {
  const mk = anyKeyInMonth.slice(0, 7)
  return (book.days || []).filter((d) => !hiddenDays[d.key] && d.key.slice(0, 7) === mk && (!hasId(threadId) || dayOnThread(d, threadId))).map((d) => d.key).reverse()
}

// ── the entry ───────────────────────────────────────────────────────────────
export function plainEntry (html) {
  return String(html || '').replace(/<br\s*\/?>/gi, ' ').replace(/<\/(p|div|li)>/gi, ' ').replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ').trim()
}
export const splitWords = (t) => String(t || '').split(/(\s+)/).filter((w) => w !== '')

export const ANSWERS = [
  { v: 'held', label: 'It held', mark: 'held' },
  { v: 'partly', label: 'Partly', mark: 'partly held' },
  { v: 'not', label: 'Not this time', mark: 'not this time' },
]
export const answerMark = (v) => (ANSWERS.find((a) => a.v === v) || {}).mark || ''

// ── search ──────────────────────────────────────────────────────────────────
function haystack (day) {
  const out = []
  for (const m of day.moments || []) {
    if (m.hidden) continue
    out.push([lineText(m), m.title, m.text, m.quote, m.card && m.card.name, ...(m.cards || []).map((c) => c && c.name),
      m.mystic && m.mystic.name, m.question].filter(Boolean).join(' '))
  }
  if (day.entry) out.push(plainEntry(day.entry.text))
  // The practice: the seeker's own lines and the threads' names (DESIGN §4).
  if (day.own) out.push(...[day.own.gut, day.own.landed].filter(Boolean))
  for (const m of day.meaning || []) out.push(m.line)
  for (const t of day.threads || []) out.push(t.name)
  return out
}

/** Pages matching q, newest first, with the words around the first hit. */
export function searchBook (book, q, { hiddenDays = {} } = {}) {
  const needle = String(q || '').trim().toLowerCase()
  if (!needle) return []
  const res = []
  for (const day of [...(book.days || [])].reverse()) {
    if (hiddenDays[day.key]) continue
    for (const t of haystack(day)) {
      const i = t.toLowerCase().indexOf(needle)
      if (i < 0) continue
      const s = Math.max(0, i - 40)
      res.push({ key: day.key, before: (s ? '…' : '') + t.slice(s, i), hit: t.slice(i, i + needle.length), after: t.slice(i + needle.length, i + needle.length + 60) + (t.length > i + needle.length + 60 ? '…' : '') })
      break
    }
  }
  return res
}

// ── edits (copy-on-write, so a failed call can put the old book back) ───────
function mapDay (book, key, fn) {
  return { ...book, days: book.days.map((d) => (d.key === key ? fn(d) : d)) }
}
export function withoutMoment (book, key, id) {
  return mapDay(book, key, (d) => ({ ...d, moments: d.moments.filter((m) => m.id !== id) }))
}
export function withMoment (book, key, id, patch) {
  return mapDay(book, key, (d) => ({ ...d, moments: d.moments.map((m) => (m.id === id ? { ...m, ...patch } : m)) }))
}
export function withEntry (book, key, patch) {
  return mapDay(book, key, (d) => ({ ...d, entry: d.entry ? { ...d.entry, ...patch } : d.entry }))
}
export function withoutDay (book, key) {
  return { ...book, days: book.days.filter((d) => d.key !== key) }
}
